import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import Swal from 'sweetalert2';
import { AuthService } from '../core/auth.service';
import { StaffService } from '../pages/staff/staff.service';
import { UserService } from '../pages/users/user.service';
import { filter, Subscription } from 'rxjs';
import { CommunicationService } from '../pages/communication/communication.service';

interface SubMenuItem {
  label: string;
  link: string;
  permissions?: string[];
}

interface MenuItem {
  label: string;
  icon: string;
  link?: string;
  permissions?: string[];
  children?: SubMenuItem[];
  expanded?: boolean;
}

@Component({
  selector: 'app-portal-layout',
  templateUrl: './portal-layout.component.html',
  styleUrls: ['./portal-layout.component.scss']
})
export class PortalLayoutComponent implements OnInit, OnDestroy {
  sidebarCollapsed = false;
  currentUser: any;
  unreadMessageCount = 0;
  unreadChatCount = 0;
  missedCallCount = 0;
  private unreadPollId: any;
  private routeSub?: Subscription;
  private lastPointerActionAt = 0;

  menus: MenuItem[] = [
    { label: 'Dashboard', icon: 'dashboard', link: '/dashboard',permissions: ['dashboard']  },
    { label: 'Todo', icon: 'checklist', link: '/todo' },
    { label: 'Meetings', icon: 'event', link: '/meetings' },
    { label: 'Users', icon: 'group', link: '/users', permissions: ['manage_users'] },
    { label: 'Roles', icon: 'verified_user', link: '/roles', permissions: ['manage_roles'] },
    {
      label: 'Staff',
      icon: 'badge',
      permissions: ['staff.view', 'staff.request.view', 'staff.request.approve', 'staff.manage', 'manage_users'],
      children: [
        { label: 'Overview', link: '/staff/overview', permissions: ['staff.view', 'staff.manage', 'staff.request.view'] },
        { label: 'Operations', link: '/staff/operations', permissions: ['staff.view', 'staff.manage', 'staff.request.view', 'staff.request.approve'] },
        { label: 'Performance', link: '/staff/performance', permissions: ['staff.manage', 'staff.view', 'staff.request.approve'] },
        { label: 'Directory', link: '/staff/directory', permissions: ['staff.view', 'staff.manage'] },
        { label: 'Requests', link: '/staff/requests', permissions: ['staff.request.view', 'staff.request.create', 'staff.request.approve', 'staff.manage'] },
        { label: 'Payroll', link: '/staff/payroll', permissions: ['staff.manage', 'staff.view', 'staff.request.approve', 'manage_users', 'manage_delivery', 'manage_logistics'] }
      ]
    },
    {
      label: 'Com Hub',
      icon: 'hub',
      permissions: ['staff.message.view', 'staff.message.send', 'staff.manage'],
      children: [
        { label: 'Mails', link: '/communication/mails', permissions: ['staff.message.view', 'staff.message.send', 'staff.manage'] },
        { label: 'Chats', link: '/communication/chats', permissions: ['staff.message.view', 'staff.message.send', 'staff.manage'] },
        { label: 'Calls', link: '/communication/calls', permissions: ['staff.message.view', 'staff.message.send', 'staff.manage'] }
      ]
    },
    {
      label: 'Projects',
      icon: 'workspaces',
      permissions: ['project.view', 'project.manage', 'project.team.manage', 'project.task.manage', 'project.onboarding.manage', 'project.analytics.view', 'project.client.onboarding', 'client'],
      children: [
        { label: 'Overview', link: '/projects/overview', permissions: ['project.view', 'project.manage', 'project.task.manage', 'project.onboarding.manage'] },
        { label: 'Timeline', link: '/projects/timeline', permissions: ['project.view', 'project.manage', 'project.task.manage', 'project.onboarding.manage'] },
        { label: 'Documents', link: '/projects/documents', permissions: ['project.view', 'project.manage', 'project.task.manage', 'project.onboarding.manage', 'project.client.onboarding', 'client'] },
        { label: 'Risks', link: '/projects/risks', permissions: ['project.view', 'project.manage'] },
        { label: 'Setup', link: '/projects/setup', permissions: ['project.view', 'project.manage'] },
        { label: 'Onboarding', link: '/projects/onboarding', permissions: ['project.onboarding.manage', 'project.client.onboarding', 'client'] },
        { label: 'Tasks', link: '/projects/tasks', permissions: ['project.view', 'project.task.manage', 'project.team.manage', 'project.manage'] },
        { label: 'Analytics', link: '/projects/analytics', permissions: ['project.analytics.view', 'project.view'] }
      ]
    },
    {
      label: 'Logistics',
      icon: 'local_shipping',
      permissions: ['manage_logistics'],
      children: [
        { label: 'Overview', link: '/logistics/overview', permissions: ['manage_logistics', 'manage_delivery', 'manage_riders', 'manage_vehicles', 'manage_maintenance', 'manage_activities'] },
        { label: 'Control Tower', link: '/logistics/control-tower', permissions: ['manage_logistics', 'manage_delivery', 'manage_riders', 'manage_vehicles', 'manage_maintenance', 'manage_activities'] },
        { label: 'Tracking', link: '/logistics/tracking', permissions: ['manage_logistics', 'manage_delivery', 'manage_activities', 'dispatcher', 'rider'] },
        { label: 'Clients', link: '/logistics/clients', permissions: ['manage_logistics', 'manage_delivery'] },
        { label: 'Delivery', link: '/logistics/delivery', permissions: ['manage_delivery'] },
        { label: 'Riders', link: '/logistics/riders', permissions: ['manage_riders'] },
        { label: 'Vehicles', link: '/logistics/vehicles', permissions: ['manage_vehicles'] },
        { label: 'Maintenance', link: '/logistics/maintenance', permissions: ['manage_maintenance'] },
        { label: 'Activities', link: '/logistics/activities', permissions: ['manage_activities'] }
      ]
    },
    {
      label: 'Supply',
      icon: 'inventory_2',
      permissions: ['manage_supply', 'supply.products.manage', 'supply.dispatch.manage', 'supply.history.view', 'supply.analytics.view'],
      children: [
        { label: 'Overview', link: '/supply/overview', permissions: ['manage_supply', 'supply.products.manage', 'supply.dispatch.manage', 'supply.history.view', 'supply.analytics.view'] },
        { label: 'Control Tower', link: '/supply/control-tower', permissions: ['manage_supply', 'supply.dispatch.manage', 'supply.history.view', 'supply.analytics.view'] },
        { label: 'Catalog', link: '/supply/catalog', permissions: ['manage_supply', 'supply.products.manage', 'supply.history.view', 'supply.analytics.view'] },
        { label: 'Product', link: '/supply/products', permissions: ['manage_supply', 'supply.products.manage'] },
        { label: 'Dispatch', link: '/supply/dispatch', permissions: ['manage_supply', 'supply.dispatch.manage'] },
        { label: 'History & Analytics', link: '/supply/history-analytics', permissions: ['manage_supply', 'supply.history.view', 'supply.analytics.view'] }
      ]
    },
    { label: 'Reports', icon: 'bar_chart', link: '/reports', permissions: ['view_reports'] },
  ];

  constructor(
    private auth: AuthService,
    public router: Router,
    private staffService: StaffService,
    private userService: UserService,
    private communicationService: CommunicationService
  ) {}

  ngOnInit(): void {
    this.currentUser = this.auth.getUser();
    this.syncMobileSidebarState();
    console.log('%c[PortalLayout] 👤 Current User:', 'color: #2ecc71', this.currentUser);
    this.loadCommunicationBadges();
    this.unreadPollId = setInterval(() => this.loadCommunicationBadges(), 30000);
    this.routeSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isMobileViewport()) {
          this.sidebarCollapsed = true;
        }
      });
  }

  ngOnDestroy(): void {
    if (this.unreadPollId) {
      clearInterval(this.unreadPollId);
    }
    this.routeSub?.unsubscribe();
  }

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  handleSidebarTogglePointer(event: PointerEvent): void {
    this.recordPointerAction(event);
    this.toggleSidebar();
  }

  handleSidebarToggleClick(event: MouseEvent): void {
    if (this.shouldIgnoreSyntheticClick()) {
      event.preventDefault();
      return;
    }

    this.toggleSidebar();
  }

  toggleMenu(menu: MenuItem): void {
    if (this.sidebarCollapsed) return;
    menu.expanded = !menu.expanded;
  }

  handleMenuParentPointer(event: PointerEvent, menu: MenuItem): void {
    this.recordPointerAction(event);
    this.toggleMenu(menu);
  }

  handleMenuParentClick(event: MouseEvent, menu: MenuItem): void {
    if (this.shouldIgnoreSyntheticClick()) {
      event.preventDefault();
      return;
    }

    this.toggleMenu(menu);
  }

  hasActiveChild(menu: MenuItem): boolean {
    if (!menu.children?.length) return false;
    return menu.children.some(sub => this.router.url.startsWith(sub.link));
  }
  /** Collapse sidebar on menu click on mobile */
  handleMenuClick(): void {
    if (this.isMobileViewport()) {
      this.sidebarCollapsed = true;
    }
  }

  navigateTo(link: string | undefined): void {
    if (!link) {
      return;
    }

    this.router.navigateByUrl(link);
    this.handleMenuClick();
  }

  handleNavigatePointer(event: PointerEvent, link: string | undefined): void {
    this.recordPointerAction(event);
    this.navigateTo(link);
  }

  handleNavigateClick(event: MouseEvent, link: string | undefined): void {
    if (this.shouldIgnoreSyntheticClick()) {
      event.preventDefault();
      return;
    }

    this.navigateTo(link);
  }

  closeSidebar(): void {
    this.sidebarCollapsed = true;
  }

  /** ✅ Unified visibility logic (handles parent, children, trimming, normalization) */
  canShow(menu: MenuItem | SubMenuItem): boolean {
    const user = this.auth.getUser();
    const rawRole = user?.role || '';
    const userRole = rawRole.toLowerCase().trim(); // normalize casing & trim
    const userPerms = (this.auth.getPermissions() || []).map(p => p.toLowerCase().trim());
    const effectivePerms = new Set([...userPerms, userRole]);

    // console.log('%c[canShow] Checking menu item:', 'color: #3498db', menu.label);
    // console.log('   ├─ Required:', menu.permissions || 'None');
    // console.log('   ├─ User Role:', userRole);
    // console.log('   ├─ User Perms:', userPerms);
    // console.log('   └─ Effective:', Array.from(effectivePerms));

    // Admin always sees everything
    if (this.auth.isAdmin()) {
      console.log(`   ✅ Shown (Admin override)`);
      return true;
    }

    // No specific permission required
    if (!menu.permissions || menu.permissions.length === 0) {
      console.log(`   ✅ Shown (no specific permission)`);
      return true;
    }

    // Match by role or permissions
    const allowed = menu.permissions.some(p => effectivePerms.has(p.toLowerCase().trim()));

    if (allowed) {
      console.log(`   ✅ Shown (matched permission or role)`);
      return true;
    }

    // Special case: check children visibility for parent menus
    const item = menu as MenuItem;
    if (item.children && item.children.length > 0) {
      const childVisible = item.children.some(child => this.canShow(child));
      if (childVisible) {
        console.log(`   ✅ Parent "${item.label}" shown because a child is visible`);
        return true;
      }
    }

    //console.warn(`   ❌ Hidden (no match)`);
    return false;
  }

  logout(): void {
    console.log('%c[PortalLayout] 🚪 Logging out user...', 'color: #e74c3c');
    this.auth.logout();
    this.router.navigate(['/auth/login']);
  }

  async openChangePassword(): Promise<void> {
    const result = await Swal.fire({
      title: 'Change Password',
      html: `
        <input id="swal-current-password" type="password" class="swal2-input" placeholder="Current password">
        <input id="swal-new-password" type="password" class="swal2-input" placeholder="New password (min 6 chars)">
        <input id="swal-confirm-password" type="password" class="swal2-input" placeholder="Confirm new password">
      `,
      showCancelButton: true,
      confirmButtonText: 'Update Password',
      preConfirm: () => {
        const currentPassword = (document.getElementById('swal-current-password') as HTMLInputElement)?.value || '';
        const newPassword = (document.getElementById('swal-new-password') as HTMLInputElement)?.value || '';
        const confirmPassword = (document.getElementById('swal-confirm-password') as HTMLInputElement)?.value || '';

        if (!currentPassword || !newPassword || !confirmPassword) {
          Swal.showValidationMessage('All password fields are required.');
          return false;
        }
        if (newPassword.length < 6) {
          Swal.showValidationMessage('New password must be at least 6 characters.');
          return false;
        }
        if (newPassword !== confirmPassword) {
          Swal.showValidationMessage('New password and confirmation do not match.');
          return false;
        }
        if (currentPassword === newPassword) {
          Swal.showValidationMessage('New password must be different from current password.');
          return false;
        }

        return { currentPassword, newPassword };
      }
    });

    if (!result.isConfirmed) return;

    this.userService.changeMyPassword(result.value).subscribe({
      next: (res: any) => {
        Swal.fire('Success', res?.message || 'Password changed successfully.', 'success');
      },
      error: (err) => {
        Swal.fire('Error', err?.error?.message || 'Failed to change password.', 'error');
      }
    });
  }

  openMessages(): void {
    this.router.navigate(['/communication/mails']);
  }

  badgeForMenu(menu: MenuItem): number {
    if (menu.label === 'Com Hub') {
      return this.unreadMessageCount + this.unreadChatCount + this.missedCallCount;
    }
    return 0;
  }

  badgeForSubmenu(sub: SubMenuItem): number {
    if (sub.link === '/communication/mails') return this.unreadMessageCount;
    if (sub.link === '/communication/chats') return this.unreadChatCount;
    if (sub.link === '/communication/calls') return this.missedCallCount;
    return 0;
  }

  private loadCommunicationBadges(): void {
    this.staffService.getUnreadMessageCount().subscribe({
      next: (res) => this.unreadMessageCount = Number(res?.unread || 0),
      error: () => this.unreadMessageCount = 0
    });
    this.communicationService.getBadgeCounts().subscribe({
      next: (res) => {
        this.unreadChatCount = Number(res?.unreadChats || 0);
        this.missedCallCount = Number(res?.missedCalls || 0);
      },
      error: () => {
        this.unreadChatCount = 0;
        this.missedCallCount = 0;
      }
    });
  }

  get isMobileSidebarOpen(): boolean {
    return this.isMobileViewport() && !this.sidebarCollapsed;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.syncMobileSidebarState();
  }

  private syncMobileSidebarState(): void {
    this.sidebarCollapsed = this.isMobileViewport();
  }

  private isMobileViewport(): boolean {
    return typeof window !== 'undefined' && window.innerWidth < 900;
  }

  private recordPointerAction(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.lastPointerActionAt = Date.now();
  }

  private shouldIgnoreSyntheticClick(): boolean {
    return Date.now() - this.lastPointerActionAt < 450;
  }
}
